import { FC, FunctionComponent, useRef } from "react";
import { Field, FieldRenderProps } from "react-final-form";

import {
  dataAttribute,
  DataAttributes,
  getId,
  ITag,
  useTheme
} from "@bps/fluent-ui";
import { identity } from "@libs/utils/utils.ts";
import { useSubmissionFormMetaContext } from "@ui-components/form/submission-form/context/SubmissionFormMetaContext.tsx";
import {
  StaticPicker,
  StaticPickerProps
} from "@ui-components/pickers/static-picker/StaticPicker.tsx";

import { ExposedFieldProps, getFieldErrorMessage } from "./FinalForm.tsx";
import { FormItemField, FormItemFieldProps } from "./FormItemField.tsx";
import { ReadOnlyTextField } from "./read-only-fields/ReadOnlyTextField/ReadOnlyTextField.tsx";
import { getNewPickerStyles } from "./utils.ts";

type HtmlElementType = any;

type StaticPickerAdapterProps = StaticPickerProps &
  Pick<FormItemFieldProps, "required" | "label"> & {
    fieldItemStyles?: FormItemFieldProps["styles"];
  };

export type StaticPickerFieldProps = ExposedFieldProps<
  StaticPickerProps["selectedKey"],
  HtmlElementType
> &
  StaticPickerAdapterProps & { horizontalLabel?: boolean };

type InternalStaticPickerAdapterProps = FieldRenderProps<
  StaticPickerProps["selectedKey"],
  HtmlElementType
> &
  StaticPickerAdapterProps & { readOnly?: boolean };

const StaticPickerAdapter: FunctionComponent<
  InternalStaticPickerAdapterProps
> = props => {
  const _id = useRef(getId(props.input.name));
  const theme = useTheme();

  const {
    meta,
    input,
    label,
    styles,
    loading,
    required,
    readOnly,
    disabled,
    inputProps,
    fieldItemStyles,
    horizontalLabel,
    ...pickerProps
  } = props;

  const formMeta = useSubmissionFormMetaContext();

  if (readOnly || formMeta?.readonly) {
    const getValueText = async () => {
      const options = await pickerProps.fetchDataSource();
      return options.find(x => x.key === input.value)?.name;
    };
    return (
      <ReadOnlyTextField
        value={input.value}
        getValueText={getValueText}
        label={label}
      />
    );
  }

  const hasErrorMessage = !!getFieldErrorMessage(meta);

  const onChange = (value: string | undefined, item: ITag) => {
    input.onChange(value);
    props.onChange && props.onChange(value, item);
    input.onFocus();
  };

  const _disabled = disabled || formMeta?.disabled;

  return (
    <FormItemField
      htmlFor={_id.current}
      name={input.name}
      required={required}
      label={label}
      styles={fieldItemStyles}
      disabled={_disabled}
      {...dataAttribute(DataAttributes.Loading, !!loading)}
      horizontalLabel={horizontalLabel}
    >
      <StaticPicker
        {...pickerProps}
        disabled={_disabled}
        onChange={onChange}
        selectedKey={input.value}
        inputProps={{
          ...inputProps,
          onBlur: input.onBlur,
          onFocus: input.onFocus,
          id: _id.current,
          autoComplete: "one-time-code"
        }}
        pickerSuggestionsProps={
          {
            styles: {
              root: { "& .ms-Suggestions-itemButton": { display: "block" } },
              itemButton: { display: "block" }
            },
            ...pickerProps.pickerSuggestionsProps
          } as unknown as any // for some reason styles is excluded from the interface but it still works.  I haven't attempted to update the original component as we likely will move away from this component in the future.
        }
        styles={getNewPickerStyles(
          theme,
          { hasErrorMessage, disabled: _disabled },
          styles
        )}
      />
    </FormItemField>
  );
};

/**
 * Use StaticPickerField to use StaticPicker with final-form.
 * It also wraps the picker in a FormItemField to support label and error message.
 * @param props
 */
export const StaticPickerField: FC<StaticPickerFieldProps> = props => (
  <Field
    format={identity}
    parse={identity}
    {...props}
    component={StaticPickerAdapter}
  />
);
